import type { Compare } from "@suse-o11y/compare";
import { isFunction } from "@suse-o11y/is-function";
import { type CompareDependencyList, useComparedDependencies } from "@suse-o11y/use-compared-dependencies";
import { type DependencyList, useMemo as useMemo_ } from "react";

/**
 * Exactly the same as React.useMemo.
 */
function useMemo<T>(factory: () => T, deps: DependencyList): T;

/**
 * Similar to React.useMemo but allows to customize the dependencies comparison.
 * If compare function returns `true`, the hook returns memoized value, otherwise it returns the incoming value.
 */
// biome-ignore lint/style/useUnifiedTypeSignatures: enforce the overload so that TDeps only appears when compare is provided
function useMemo<T, TDeps extends DependencyList>(factory: () => T, deps: TDeps, compare: Compare<TDeps>): T;

/**
 * Similar to React.useMemo but allows for custom per-dependency comparison.
 * If a comparator is not provided for a dependency, it falls back to `isStrictEqual`.
 * If all comparators return `true`, the hook returns memoized value, otherwise it returns the incoming value.
 */
function useMemo<T, TDeps extends DependencyList>(
	factory: () => T,
	deps: TDeps,
	comparators: CompareDependencyList<TDeps>,
): T;

/**
 * ! The hook can violate the rules of hooks because the presence and type of the compare argument never changes at runtime.
 */
function useMemo<T, TDeps extends DependencyList>(
	factory: () => T,
	deps: TDeps,
	compare?: Compare<TDeps> | CompareDependencyList<TDeps>,
): T {
	// fallbacks to regular React.useMemo to avoid unnecessary resource allocation for useComparedDependencies
	if (compare == null) {
		// biome-ignore lint/correctness/useHookAtTopLevel: the condition never changes at runtime
		return useMemo_(factory, deps);
	}

	if (isFunction(compare)) {
		// biome-ignore lint/correctness/useHookAtTopLevel: the condition never changes at runtime
		// biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are forwarded
		return useMemo(factory, useComparedDependencies(deps, compare));
	}

	// biome-ignore lint/correctness/useHookAtTopLevel: the condition never changes at runtime
	// biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are forwarded
	return useMemo(factory, useComparedDependencies(deps, compare));
}

export { useMemo };
